/**
 * Hook for handling Airwallex fraud detection
 */
import { useCallback } from 'react';
import type { Payment, PaymentOptions } from '../../../core/resources/payments';
import type { PaymentsResource } from '../../../core/resources/payments';
import type { UsePaymentOptions } from '../usePaymentQuery';

interface UseAirwallexRadarActionParams {
    paymentsResource: PaymentsResource;
    startPolling: any;
    setError: (error: string | null) => void;
    setIsLoading: (loading: boolean) => void;
    hookOptionsRef: React.MutableRefObject<UsePaymentOptions | undefined>;
}

export function useAirwallexRadarAction({
    paymentsResource,
    startPolling,
    setError,
    setIsLoading,
    hookOptionsRef,
}: UseAirwallexRadarActionParams) {
    const handleAirwallexRadar = useCallback(
        async (payment: Payment, actionData: any, options: PaymentOptions = {}, handlePaymentAction: any) => {
            // Handle Airwallex fraud detection - collect device fingerprint
            const isTest = actionData.metadata?.isTest || false;
            const orderId = payment.order?.id;
            const checkoutSessionId = payment.order?.checkoutSessionId;

            if (!orderId || !checkoutSessionId) {
                console.error('Airwallex radar: missing order or checkoutSessionId');
                setError('Missing order information for fraud detection');
                setIsLoading(false);
                options.onFailure?.('Missing order information for fraud detection');
                options.onPaymentFailed?.({
                    code: 'AIRWALLEX_RADAR_MISSING_ORDER',
                    message: 'Missing order information for fraud detection',
                    payment,
                });
                return;
            }

            try {
                // Generate a unique session ID for Airwallex device fingerprinting
                const generateAirwallexSessionId = (): string => {
                    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                        const r = (Math.random() * 16) | 0;
                        const v = c === 'x' ? r : (r & 0x3) | 0x8;
                        return v.toString(16);
                    });
                };

                const sessionId = generateAirwallexSessionId();

                // Load Airwallex fraud API script if not already loaded
                const existingScript = document.getElementById('airwallex-fraud-api');
                if (!existingScript) {
                    const script = document.createElement('script');
                    script.type = 'text/javascript';
                    script.async = true;
                    script.id = 'airwallex-fraud-api';
                    script.setAttribute('data-order-session-id', sessionId);

                    // Use demo URL for testing, production URL for live
                    const baseUrl = isTest ? 'https://static-demo.airwallex.com' : 'https://static.airwallex.com';
                    script.src = `${baseUrl}/webapp/fraud/device-fingerprint/index.js`;

                    // Wait for script to load
                    await new Promise<void>((resolve, reject) => {
                        script.onload = () => {
                            console.log('Airwallex fraud API script loaded successfully');
                            resolve();
                        };
                        script.onerror = () => {
                            reject(new Error('Failed to load Airwallex fraud API script'));
                        };

                        // Append script to body
                        document.body.appendChild(script);
                    });
                } else {
                    // Update existing script with new session ID
                    existingScript.setAttribute('data-order-session-id', sessionId);
                    console.log('Updated existing Airwallex script with new session ID');
                }

                console.log('Airwallex device fingerprint session created:', sessionId);

                // Save radar session to database
                await paymentsResource.saveRadarSession({
                    paymentId: payment.id,
                    checkoutSessionId,
                    orderId,
                    airwallexRadarSessionId: sessionId,
                });
                console.log('Airwallex radar session saved to database');

                // Resume payment by calling completePaymentAfterAction
                const resumedPayment = await paymentsResource.completePaymentAfterAction(payment.id);
                console.log('Payment resumed after Airwallex radar:', resumedPayment);
                console.log('📊 [usePayment] Resumed payment details:', {
                    status: resumedPayment.status,
                    requireAction: resumedPayment.requireAction,
                    hasRequireActionData: !!resumedPayment.requireActionData,
                    error: (resumedPayment as any).error,
                });

                // Handle the resumed payment response
                // IMPORTANT: Check for declined/failed status FIRST, before checking requireAction
                // This is because a failed 3DS can return with status=declined but still have requireAction set
                if (resumedPayment.status === 'declined' || resumedPayment.status === 'failed') {
                    // Payment declined or failed - extract error message from response
                    const errorMsg = (resumedPayment as any).error?.message
                        || (resumedPayment as any).error?.processorMessage
                        || 'Payment declined';
                    console.error('❌ [usePayment] Payment declined after Airwallex radar:', errorMsg);
                    setError(errorMsg);
                    setIsLoading(false);
                    options.onFailure?.(errorMsg);
                    options.onPaymentFailed?.({
                        code: (resumedPayment as any).error?.code || 'PAYMENT_DECLINED',
                        message: errorMsg,
                        payment: resumedPayment,
                    });

                    // Hook-level callback (universal handler)
                    if (hookOptionsRef.current?.onPaymentFailed) {
                        hookOptionsRef.current.onPaymentFailed(errorMsg, {
                            isRedirectReturn: false,
                        });
                    }
                } else if (resumedPayment.status === 'succeeded') {
                    // Payment succeeded
                    setIsLoading(false);
                    const response = {
                        paymentId: resumedPayment.id,
                        payment: resumedPayment,
                        order: (resumedPayment as any).order,
                    };

                    // Hook-level callback (universal handler)
                    if (hookOptionsRef.current?.onPaymentCompleted) {
                        await hookOptionsRef.current.onPaymentCompleted(resumedPayment, {
                            isRedirectReturn: false,
                            order: response.order,
                        });
                    }

                    options.onSuccess?.(response);
                    options.onPaymentSuccess?.(response);
                } else if (resumedPayment.requireAction !== 'none' && resumedPayment.requireActionData) {
                    // Payment requires another action (e.g., 3DS)
                    await handlePaymentAction(resumedPayment, options);
                } else {
                    // Start polling for final status
                    startPolling(resumedPayment.id, {
                        onRequireAction: (updatedPayment: Payment) => {
                            void handlePaymentAction(updatedPayment, options);
                        },
                        onSuccess: async (successPayment: Payment) => {
                            setIsLoading(false);
                            const response = {
                                paymentId: successPayment.id,
                                payment: successPayment,
                                order: (successPayment as any).order,
                            };

                            // Hook-level callback (universal handler)
                            if (hookOptionsRef.current?.onPaymentCompleted) {
                                await hookOptionsRef.current.onPaymentCompleted(successPayment, {
                                    isRedirectReturn: false,
                                    order: response.order,
                                });
                            }

                            options.onSuccess?.(response);
                            options.onPaymentSuccess?.(response);
                        },
                        onFailure: async (errorMsg: string) => {
                            setError(errorMsg);
                            setIsLoading(false);

                            // Hook-level callback (universal handler)
                            if (hookOptionsRef.current?.onPaymentFailed) {
                                await hookOptionsRef.current.onPaymentFailed(errorMsg, {
                                    isRedirectReturn: false,
                                });
                            }

                            options.onFailure?.(errorMsg);
                            options.onPaymentFailed?.({
                                code: 'PAYMENT_FAILED',
                                message: errorMsg,
                                payment,
                            });
                        },
                    });
                }
            } catch (radarError) {
                const errorMsg = radarError instanceof Error ? radarError.message : 'Airwallex fraud detection failed';
                console.error('Airwallex radar error:', radarError);
                setError(errorMsg);
                setIsLoading(false);
                options.onFailure?.(errorMsg);
                options.onPaymentFailed?.({
                    code: 'AIRWALLEX_RADAR_FAILED',
                    message: errorMsg,
                    payment,
                });
            }
        },
        [paymentsResource, startPolling, setError, setIsLoading, hookOptionsRef],
    );

    return { handleAirwallexRadar };
}
